import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import DocumentsTab from "./DocumentsTab";

interface DocumentsTabWrapperProps {
  linkType: "project" | "phase" | "activity" | "task" | "issue";
  shouldLoadData?: boolean; // Control when to load data
  isFromWorkUI?: boolean;
  parentDrawerMode?: boolean; // When true, component is inside another drawer
}

const DocumentsTabWrapper: React.FC<DocumentsTabWrapperProps> = ({
  linkType,
  shouldLoadData = true, // Default to true for backward compatibility
  isFromWorkUI = false, // New prop to indicate if it's from Work UI
  parentDrawerMode = false, // When inside another drawer
}) => {
  // Get data from different store slices based on linkType
  const { data: ActivityInDetailModal } = useSelector(
    (state: RootState) => state.ActivityInDetailModal
  );
  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );
  const { data: PhaseInDetailModal } = useSelector(
    (state: RootState) => state.PhaseInDetailModal
  );

  const { data: IssueListInDetailModal } = useSelector(
    (state: RootState) => state.IssueListInDetailModal
  );

  // Extract data based on linkType
  const getDocumentData = () => {
    switch (linkType) {
      case "activity": {
        const {
          id: activityId,
          activityName,
          projectResponse: activityProjectResponse,
          phaseResponse: activityPhaseResponse,
        } = ActivityInDetailModal || {};
        return {
          linkToId: activityId,
          linkToName: activityName,
          projectId: activityProjectResponse?.id || undefined,
          projectName: activityProjectResponse?.name,
          phaseName: activityPhaseResponse?.name,
          activityName: activityName,
        };
      }

      case "task": {
        const {
          selectedTaskDetails,
          activityResponse,
          projectResponse: taskProjectResponse,
          phaseResponse: taskPhaseResponse,
        } = TaskListInDetailModal || {};
        return {
          linkToId: selectedTaskDetails?.taskId,
          linkToName: selectedTaskDetails?.taskName,
          projectId: taskProjectResponse?.id || undefined,
          projectName: taskProjectResponse?.name,
          phaseName: taskPhaseResponse?.name,
          activityName: activityResponse?.name,
          taskName: selectedTaskDetails?.taskName,
        };
      }

      case "phase": {
        const {
          id: phaseId,
          phaseName: phaseNameFromStore,
          projectResponse: phaseProjectResponse,
        } = PhaseInDetailModal || {};
        return {
          linkToId: phaseId,
          linkToName: phaseNameFromStore,
          projectId: phaseProjectResponse?.id || undefined,
          projectName: phaseProjectResponse?.name,
          phaseName: phaseNameFromStore,
        };
      }

      case "issue": {
        const {
          selectedIssueDetails,
          projectResponse: issueProjectResponse,
          phaseResponse: issuePhaseResponse,
        } = IssueListInDetailModal || {};

        return {
          linkToId: selectedIssueDetails?.issueId,
          linkToName: selectedIssueDetails?.issueName,
          projectId: issueProjectResponse?.id || undefined,
          projectName: issueProjectResponse?.name,
          phaseName: issuePhaseResponse?.name,
          issueName: selectedIssueDetails?.issueName,
        };
      }

      default:
        return {
          linkToId: "",
          linkToName: "",
          projectId: undefined,
          projectName: undefined,
          phaseName: undefined,
          activityName: undefined,
          taskName: undefined,
        };
    }
  };

  return (
    <DocumentsTab
      linkType={linkType}
      shouldLoadData={shouldLoadData}
      isFromWorkUI={isFromWorkUI}
      parentDrawerMode={parentDrawerMode}
      {...getDocumentData()}
    />
  );
};

export default DocumentsTabWrapper;
